// src/services/causasApi.ts
import type { CausaJudicial, Sancion, Testigo, Involucrado, CausaArchivo } from '../views/JuezMunicipal/types';

export const API_BASE = `${process.env.REACT_APP_API_URL}/api`;


type CreateResponse = { ok: boolean; data: { id: number } & any };
type GetResponse = { ok: boolean; data: any };
type UploadFilesResponse = { ok: boolean; data: any[] };
function numOrUndef(v: any) {
  if (v === null || v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
}
function parseSize(v: unknown): number | undefined {
  if (v == null) return undefined;
  const n = Number(v as any);
  return Number.isFinite(n) ? n : undefined;
}


function groupEvidencias(archivos: CausaArchivo[]): {
  fotos: string[];
  pdfs: string[];
  actaInfraccionPdfPath?: string;
  ordenGruaPdfPath?: string;
} {
  const fotos = archivos
    .filter(a => a.mime?.startsWith('image/') || a.tipo === 'FOTO')
    .map(a => a.url);

  const pdfs = archivos
    .filter(a => a.mime === 'application/pdf' && a.tipo === 'PDF')
    .map(a => a.url);

  const acta = archivos.find(a => a.tipo === 'ACTA_INFRACCION');
  const orden = archivos.find(a => a.tipo === 'ORDEN_GRUA');

  return {
    fotos,
    pdfs,
    actaInfraccionPdfPath: acta?.url,
    ordenGruaPdfPath: orden?.url,
  };
}

export function mapFromBackend(d: any): CausaJudicial {
  // 1) Mapear archivos
  const archivos: CausaArchivo[] = (d.CausaArchivo || []).map((a: any) => ({
    id: a.id,
    causaId: a.causaId,
    tipo: a.tipo,
    url: a.url,
    mime: a.mime,
    filename: a.filename,
    originalName: a.original_name,
    size: parseSize(a.size),         // BigInt/string -> number (opcional)
    createdAt: a.createdAt,
  }));

  // 2) Derivar evidencias (opcional)
  const evidenciasDeriv = groupEvidencias(archivos);

  return {
    id: d.id,
    expediente: d.expediente,
    folio: d.folio ?? undefined,
    numeroActa: d.numero_acta ?? undefined,

    tipoCausa: d.tipo_causa,
    estado: d.estado,

    fechaIngreso: d.fecha_ingreso ?? undefined,
    horaIngreso: d.hora_ingreso ?? undefined,
    fechaHoraLevantamiento: d.fecha_hora_levantamiento ?? undefined,
    funcionarioQueLevanta: d.funcionario_que_levanta ?? undefined,

    infractor: d.infractor ?? undefined,
    denunciante: d.denunciante ?? undefined,
    normativa: d.normativa ?? undefined,
    descripcionDetalladaHtml: d.descripcion_html ?? '',
    observaciones: d.observaciones ?? undefined,

    tipoMulta: d.tipo_multa ?? undefined,
    pagosMulta: numOrUndef(d.pagos_multa),
    descuento: numOrUndef(d.descuento),
    condonacionTotal: !!d.condonacion_total,
    ordenadoPor: d.ordenado_por ?? undefined,

    faltaCivica: d.falta_civica ?? undefined,
    nomenclatura: d.nomenclatura ?? undefined,

    conductor: d.conductor_json ? JSON.parse(d.conductor_json) : undefined,
    vehiculo: d.vehiculo_json ? JSON.parse(d.vehiculo_json) : undefined,
    propietario: d.propietario_json ? JSON.parse(d.propietario_json) : undefined,

    lugarInfraccion: d.lugar_infraccion ?? undefined,
    garantia: d.garantia ?? undefined,
    usarMismosDatosVehiculo: !!d.usar_mismos_datos_vehiculo,

    lugarHecho:
      (d.lugar_direccion || d.lugar_colonia || d.lugar_referencias || d.lugar_lat != null || d.lugar_lng != null)
        ? {
            direccion: d.lugar_direccion ?? undefined,
            colonia: d.lugar_colonia ?? undefined,
            referencias: d.lugar_referencias ?? undefined,
            lat: d.lugar_lat ?? null,
            lng: d.lugar_lng ?? null,
          }
        : undefined,

    elementoPolicialNombre: d.elemento_policial_nombre ?? undefined,
    elementoPolicialCredencial: d.elemento_policial_credencial ?? undefined,
    montoSancion: numOrUndef(d.monto_sancion),

    sanciones: (d.CausaSancion || []).map((s: any) => ({
      numero: s.numero ?? undefined,
      normativa: s.normativa ?? undefined,
      fundamento: s.fundamento ?? undefined,
      observaciones: s.observaciones ?? undefined,
    })),

    testigos: (d.CausaTestigo || []).map((t: any) => ({
      nombreCompleto: t.nombre_completo,
      folioINE: t.folio_ine ?? undefined,
      curp: t.curp ?? undefined,
      telefono: t.telefono ?? undefined,
    })),

    involucrados: (d.CausaInvolucrado || []).map((i: any) => ({
      nombreCompleto: i.nombre_completo,
      domicilio: i.domicilio ?? undefined,
      telefono: i.telefono ?? undefined,
      correo: i.correo ?? undefined,
    })),

    // ⬇️ NUEVO: conserva el arreglo para compatibilidad
    CausaArchivo: archivos,

    // ⬇️ NUEVO: si quieres un campo de conveniencia para tu UI
  };
}


function mapToBackend(c: CausaJudicial) {
  // OJO: el backend esperaba snake_case.
  return {
    expediente: c.expediente,
    folio: c.folio ?? null,
    numero_acta: c.numeroActa ?? null,

    tipo_causa: c.tipoCausa,
    estado: c.estado,

    fecha_ingreso: c.fechaIngreso || null,                // YYYY-MM-DD o ISO
    hora_ingreso: c.horaIngreso || null,
    fecha_hora_levantamiento: c.fechaHoraLevantamiento || null,
    funcionario_que_levanta: c.funcionarioQueLevanta || null,

    infractor: c.infractor || null,
    denunciante: c.denunciante || null,
    normativa: c.normativa || null,
    descripcion_html: c.descripcionDetalladaHtml || '',
    observaciones: c.observaciones || null,

    tipo_multa: c.tipoMulta || null,
    pagos_multa: c.pagosMulta ?? null,
    descuento: c.descuento ?? null,
    condonacion_total: !!c.condonacionTotal,
    ordenado_por: c.ordenadoPor || null,

    falta_civica: c.faltaCivica || null,
    nomenclatura: c.nomenclatura || null,

    // JSONs (backend los parsea con JSON.parse, así que se los mandamos stringificados)
    conductor_json: c.conductor ? JSON.stringify(c.conductor) : null,
    vehiculo_json: c.vehiculo ? JSON.stringify(c.vehiculo) : null,
    propietario_json: c.propietario ? JSON.stringify(c.propietario) : null,

    lugar_infraccion: c.lugarInfraccion || null,
    garantia: c.garantia || null,
    usar_mismos_datos_vehiculo: !!c.usarMismosDatosVehiculo,

    lugar_direccion: c.lugarHecho?.direccion || null,
    lugar_colonia: c.lugarHecho?.colonia || null,
    lugar_referencias: c.lugarHecho?.referencias || null,
    lugar_lat: c.lugarHecho?.lat ?? null,
    lugar_lng: c.lugarHecho?.lng ?? null,

    elemento_policial_nombre: c.elementoPolicialNombre || null,
    elemento_policial_credencial: c.elementoPolicialCredencial || null,
    monto_sancion: c.montoSancion ?? null,

    // RELACIONES: el controlador mapea body.sanciones/testigos/involucrados
    sanciones: (c.sanciones || []).map((s: Sancion) => ({
      numero: s.numero ?? null,
      normativa: s.normativa ?? null,
      fundamento: s.fundamento ?? null,
      observaciones: s.observaciones ?? null,
    })),
    testigos: (c.testigos || []).map((t: Testigo) => ({
      nombreCompleto: t.nombreCompleto,
      folioINE: (t as any).folioINE ?? null,
      curp: (t as any).curp ?? null,
      telefono: (t as any).telefono ?? null,
    })),
    involucrados: (c.involucrados || []).map((i: Involucrado) => ({
      nombreCompleto: i.nombreCompleto,
      domicilio: i.domicilio ?? null,
      telefono: i.telefono ?? null,
      correo: i.correo ?? null,
    })),
  };
}

export async function createCausa(c: CausaJudicial) : Promise<{ id: number; raw: any }> {
  const payload = mapToBackend(c);

  const res = await fetch(`${API_BASE}/causas`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const json = (await res.json()) as CreateResponse;
  if (!res.ok || !json.ok) {
    throw new Error((json as any).error || 'Error creando causa');
  }
  return { id: json.data.id, raw: json.data };
}

export async function getCausa(id: number): Promise<CausaJudicial> {
  const res = await fetch(`${API_BASE}/causas/${id}`);
  const json = await res.json();
  // si tu API regresa { ok, data }, usa json.data
  return mapFromBackend(json.data ?? json);
}


export async function updateCausa(id: number, payload: Partial<CausaJudicial>): Promise<void> {
  const res = await fetch(`${API_BASE}/causas/${id}`, {
    method: 'PUT', // o 'PATCH' según tu backend
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Error al actualizar la causa');
}

// Asegúrate de que esta función reciba number:
export async function uploadCausaFiles(
  id: number,
  files: { acta?: File | null; orden?: File | null; pdfs?: File[]; fotos?: File[] }
): Promise<void> {
  const fd = new FormData();
  if (files.acta) fd.append('actaInfraccionPdf', files.acta);
  if (files.orden) fd.append('orden', files.orden);
  (files.pdfs ?? []).forEach(f => fd.append('pdfs', f));
  (files.fotos ?? []).forEach(f => fd.append('fotos', f));

  const res = await fetch(`${API_BASE}/causas/${id}/archivos`, { method: 'POST', body: fd });
  if (!res.ok) throw new Error('Error al subir archivos');
}

export async function listCausas(): Promise<CausaJudicial[]> {
  const res = await fetch(`${API_BASE}/causas`);
  const json = await res.json();
  if (!res.ok || !json.ok) throw new Error(json.error || 'No se pudo obtener la lista de causas');
  return (json.data as any[]).map(mapFromBackend);
}


export type SeguimientoPayload = {
  fecha?: string;             // ISO "YYYY-MM-DDTHH:mm"
  nota?: string;
  estado_nuevo?: string | null;
  firmado?: boolean;
};

// Por causaId
export async function listSeguimientos(causaId: number) {
  const r = await fetch(`${API_BASE}/causas/${causaId}/seguimientos`);
  if (!r.ok) throw new Error('Error al listar seguimientos');
  return (await r.json()).data;
}

export async function createSeguimiento(causaId: number, payload: SeguimientoPayload) {
  const r = await fetch(`${API_BASE}/causas/${causaId}/seguimientos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) throw new Error('Error al crear seguimiento');
  return (await r.json()).data;
}

export async function updateSeguimientoApi(causaId: number, segId: number, payload: SeguimientoPayload) {
  const r = await fetch(`${API_BASE}/causas/${causaId}/seguimientos/${segId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) throw new Error('Error al actualizar seguimiento');
  return (await r.json()).data;
}

export async function deleteSeguimientoApi(causaId: number, segId: number) {
  const r = await fetch(`${API_BASE}/causas/${causaId}/seguimientos/${segId}`, { method: 'DELETE' });
  if (!r.ok) throw new Error('Error al eliminar seguimiento');
  return true;
}

// Alternativas por expediente (encajan con tu ModalSeguimiento actual)
export async function createSeguimientoByExpediente(expediente: string, payload: SeguimientoPayload) {
  const r = await fetch(`${API_BASE}/causas/expediente/${encodeURIComponent(expediente)}/seguimientos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) throw new Error('Error al crear seguimiento por expediente');
  return (await r.json()).data;
}

export async function listSeguimientosByExpediente(expediente: string) {
  const r = await fetch(`${API_BASE}/causas/expediente/${encodeURIComponent(expediente)}/seguimientos`);
  if (!r.ok) throw new Error('Error al listar seguimientos por expediente');
  return (await r.json()).data;
}

// export async function getSeguimientosByExpediente(expediente: string): Promise<SeguimientoCausa[]> {
//   const url = `${API_BASE}/causas/expediente/${encodeURIComponent(expediente)}/seguimientos`;
//   const { data } = await axios.get(url);

//   // El backend que te propuse devuelve { ok: true, data: [...] }
//   const rows: any[] = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];

//   // Normalizamos a tu tipo del front (estadoNuevo vs estado_nuevo, etc.)
//   return rows.map((r: any) => ({
//     id: r.id,
//     causaExpediente: expediente,
//     fecha: typeof r.fecha === 'string' ? r.fecha : new Date(r.fecha).toISOString(),
//     nota: r.nota ?? undefined,
//     estadoNuevo: r.estado_nuevo ?? undefined,
//     firmado: !!r.firmado,
//   }));
// }